import { useEffect, useState, type FormEvent } from "react";
import { Settings } from "lucide-react";

import type { Fraktion } from "@/components/atomic-crm/misc/fraktionen";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";

import type { VoltRoles } from "../navigation/voltAreas";
import { VoltEmpty } from "../ui/VoltEmpty";
import { VoltPageHeader } from "../ui/VoltPageHeader";
import {
  attendanceErrorText,
  salesName,
  useAttendanceSettings,
  useDistrictSales,
  useSaveMemberFunction,
  useSaveSettings,
  useSignatureUrl,
  useUploadSignature,
  type SalesPerson,
} from "./attendanceApi";
import { signaturePath } from "./attendanceLogic";
import { AttendanceAccess, AttendanceFrame } from "./AttendanceFrame";
import type { AttendanceMember, AttendanceSettings } from "./types";

const NONE = "none";
const IMAGE_TYPES = ["image/png", "image/jpeg"];

const Feedback = ({
  error,
  saved,
}: {
  error: string | null;
  saved: boolean;
}) =>
  error ? (
    <p role="alert" className="text-sm text-destructive">
      {error}
    </p>
  ) : saved ? (
    <p role="status" className="text-sm text-muted-foreground">
      Gespeichert.
    </p>
  ) : null;

const RecipientsForm = ({
  settings,
  sales,
  superadmin,
}: {
  settings: AttendanceSettings;
  sales: SalesPerson[];
  superadmin: boolean;
}) => {
  const save = useSaveSettings();
  const [form, setForm] = useState({
    bv_recipient: settings.bv_recipient ?? "",
    test_recipient: settings.test_recipient ?? "",
    signatory_sales_id: settings.signatory_sales_id
      ? String(settings.signatory_sales_id)
      : NONE,
    signatory_function: settings.signatory_function,
    live: settings.live,
  });
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const set = (patch: Partial<typeof form>) => {
    setForm((f) => ({ ...f, ...patch }));
    setSaved(false);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await save.mutateAsync({
        district: settings.district,
        patch: {
          bv_recipient: form.bv_recipient.trim() || null,
          test_recipient: form.test_recipient.trim() || null,
          signatory_sales_id:
            form.signatory_sales_id === NONE
              ? null
              : Number(form.signatory_sales_id),
          signatory_function: form.signatory_function.trim(),
          ...(superadmin ? { live: form.live } : {}),
        },
      });
      setSaved(true);
    } catch (err) {
      setError(attendanceErrorText(err));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Meldung an die BV</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="bv-recipient">Empfänger bei der BV</Label>
              <Input
                id="bv-recipient"
                type="email"
                value={form.bv_recipient}
                onChange={(e) => set({ bv_recipient: e.target.value })}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="test-recipient">Testempfänger</Label>
              <Input
                id="test-recipient"
                type="email"
                value={form.test_recipient}
                onChange={(e) => set({ test_recipient: e.target.value })}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="signatory">Fraktionsgeschäftsführer</Label>
              <Select
                value={form.signatory_sales_id}
                onValueChange={(v) => set({ signatory_sales_id: v })}
              >
                <SelectTrigger id="signatory" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Niemand gewählt</SelectItem>
                  {sales
                    .filter((s) => !s.disabled)
                    .map((s) => (
                      <SelectItem key={s.id} value={String(s.id)}>
                        {salesName(s)}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="signatory-function">
                Funktion unter der Unterschrift
              </Label>
              <Input
                id="signatory-function"
                value={form.signatory_function}
                onChange={(e) => set({ signatory_function: e.target.value })}
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Switch
              id="live"
              checked={form.live}
              disabled={!superadmin}
              onCheckedChange={(live) => set({ live })}
            />
            <Label htmlFor="live">Livebetrieb: Meldungen gehen an die BV</Label>
          </div>
          {!superadmin ? (
            <p className="text-sm text-muted-foreground">
              Den Livebetrieb schaltet nur ein Superadmin. Bis dahin gehen
              Meldungen an den Testempfänger.
            </p>
          ) : null}
          <div className="flex items-center gap-3">
            <Button type="submit" disabled={save.isPending}>
              Speichern
            </Button>
            <Feedback error={error} saved={saved} />
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

const SignatureForm = ({ settings }: { settings: AttendanceSettings }) => {
  const upload = useUploadSignature();
  const save = useSaveSettings();
  const preview = useSignatureUrl(settings.signature_path);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const onFile = async (file: File | undefined) => {
    setError(null);
    setSaved(false);
    if (!file) return;
    if (!IMAGE_TYPES.includes(file.type)) {
      setError("Bitte eine PNG- oder JPG-Datei wählen.");
      return;
    }
    try {
      const path = await upload.mutateAsync({
        path: signaturePath(settings.district, file),
        file,
      });
      await save.mutateAsync({
        district: settings.district,
        patch: { signature_path: path },
      });
      setSaved(true);
    } catch (err) {
      setError(attendanceErrorText(err));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Unterschrift</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {settings.signature_path ? (
          preview.data ? (
            <img
              src={preview.data}
              alt="Hinterlegte Unterschrift"
              className="h-20 w-fit rounded border bg-white object-contain p-2"
            />
          ) : (
            <Skeleton className="h-20 w-48" />
          )
        ) : (
          <p className="text-sm text-muted-foreground">
            Noch keine Unterschrift hinterlegt.
          </p>
        )}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="signature">
            Neue Unterschrift hochladen (PNG oder JPG)
          </Label>
          <Input
            id="signature"
            type="file"
            accept="image/png,image/jpeg"
            disabled={upload.isPending || save.isPending}
            onChange={(e) => {
              void onFile(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
        </div>
        <Feedback error={error} saved={saved} />
      </CardContent>
    </Card>
  );
};

const MemberRow = ({
  person,
  member,
  district,
}: {
  person: SalesPerson;
  member: AttendanceMember | undefined;
  district: Fraktion;
}) => {
  const save = useSaveMemberFunction();
  const [value, setValue] = useState(member?.function ?? "");
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setValue(member?.function ?? ""), [member?.function]);
  const dirty = value.trim() !== (member?.function ?? "");
  const id = `function-${person.id}`;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await save.mutateAsync({
        district,
        salesId: person.id,
        memberId: member?.id ?? null,
        fn: value.trim(),
      });
    } catch (err) {
      setError(attendanceErrorText(err));
    }
  };

  return (
    <li>
      <form
        onSubmit={submit}
        className="flex flex-col gap-2 border-b py-3 sm:flex-row sm:items-center"
      >
        <Label htmlFor={id} className="sm:w-56">
          {salesName(person)}
        </Label>
        <Input
          id={id}
          value={value}
          placeholder="z. B. Fraktionsvorsitzende"
          onChange={(e) => setValue(e.target.value)}
          className="sm:max-w-sm"
        />
        <Button
          type="submit"
          size="sm"
          variant="outline"
          disabled={!dirty || save.isPending}
        >
          Speichern
        </Button>
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
      </form>
    </li>
  );
};

const SettingsBody = ({
  roles,
  district,
}: {
  roles: VoltRoles;
  district: Fraktion;
}) => {
  const data = useAttendanceSettings(district);
  const sales = useDistrictSales(district);
  if (data.isPending || sales.isPending)
    return <Skeleton className="h-60 w-full" />;
  if (data.error || sales.error)
    return (
      <p role="alert" className="text-sm text-destructive">
        Die Einstellungen konnten nicht geladen werden.
      </p>
    );
  const { settings, members } = data.data;
  if (!settings)
    return (
      <VoltEmpty
        icon={Settings}
        title="Anwesenheit ist für diese Fraktion noch nicht eingerichtet"
        description="Kalender und Muster legt ein Superadmin beim Einrichten an."
      />
    );
  const byPerson = new Map(members.map((m) => [m.sales_id, m]));
  const people = sales.data.filter((s) => !s.disabled || byPerson.has(s.id));

  return (
    <div className="flex flex-col gap-6">
      <RecipientsForm
        key={settings.updated_at}
        settings={settings}
        sales={sales.data}
        superadmin={roles.superadmin}
      />
      <SignatureForm settings={settings} />
      <Card>
        <CardHeader>
          <CardTitle>Funktionen je Person</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-2 text-sm text-muted-foreground">
            Steht in der Spalte „Funktion“ der Anwesenheitsliste.
          </p>
          <ul>
            {people.map((p) => (
              <MemberRow
                key={p.id}
                person={p}
                member={byPerson.get(p.id)}
                district={district}
              />
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export const AttendanceSettingsPage = () => {
  return (
    <AttendanceFrame title="Einstellungen" back="/attendance">
      <AttendanceAccess>
        {({ roles, district }) => (
          <>
            <VoltPageHeader
              title="Anwesenheit · Einstellungen"
              description="Empfänger, Unterschrift und Funktionen für die Meldung an die BV."
            />
            <SettingsBody roles={roles} district={district} />
          </>
        )}
      </AttendanceAccess>
    </AttendanceFrame>
  );
};

AttendanceSettingsPage.path = "/attendance/settings";
